import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Coffee,
  ListPlus,
  Minimize2,
  Pause,
  Phone,
  Play,
  Plus,
  Square,
  Undo2,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { useCurrentUser } from '@/features/auth/useAuth';
import { blockKeys } from '@/features/calendars/useTimeBlocks';
import { useCompletionNote } from '@/features/tasks/CompletionNote';
import { taskKeys } from '@/features/tasks/useTasks';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { ClientTimerPicker } from './ClientTimerPicker';
import { BacklogAddPicker, TaskResumeSheet } from './TaskResumeSheet';
import { WorkBlockPlanner } from './WorkBlockPlanner';
import {
  formatCountdown,
  formatHms,
  useCountdown,
  useFocus,
  useFocusControls,
  useFocusSessionSync,
} from './useFocus';
import { formatClock, useElapsed } from './useTimer';
import {
  useWorkBlock,
  useWorkBlockControls,
  useWorkBlockSuggestions,
  type BlockActivity,
  type UnlabeledClientEntry,
  type WorkBlock,
  type WorkBlockSuggestion,
} from './useWorkBlocks';

interface FocusBlockPageProps {
  blockId: number;
  onExit: () => void;
  onMinimize?: () => void;
}

const PHASE_LABELS: Record<string, string> = {
  planning: 'Choose tasks',
  sprint: 'Sprint',
  break_pending: 'Break?',
  break: 'Break',
  return_pending: 'Back to work',
  wind_down: 'Wind down',
  review: 'Review',
};

export function FocusBlockPage({ blockId, onExit, onMinimize }: FocusBlockPageProps) {
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const block = useWorkBlock(blockId);
  const suggestions = useWorkBlockSuggestions(blockId);
  const focus = useFocus();
  const focusControls = useFocusControls();
  const askForNote = useCompletionNote();
  const controls = useWorkBlockControls();

  const [selected, setSelected] = useState<WorkBlockSuggestion[]>([]);
  const [lightDay, setLightDay] = useState(false);
  const [clientId, setClientId] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [backlogPickerOpen, setBacklogPickerOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeMeta, setResumeMeta] = useState<{
    id: number | null;
    title: string | null;
    reason: 'complete' | 'client_end';
  }>({ id: null, title: null, reason: 'complete' });
  const [capture, setCapture] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmEndEarly, setConfirmEndEarly] = useState(false);
  const [seededSelection, setSeededSelection] = useState(false);

  const qc = useQueryClient();
  const {
    confirmPlanning,
    confirmBreak,
    endBreak,
    confirmReturn,
    extend,
    endEarly,
    cancel: cancelBlock,
    finish,
    updateItems,
    addItem,
    captureTask,
  } = controls;

  const exitBlock = () => {
    void focus.refetch();
    onExit();
  };

  const finishAndExit = (carryIds: number[], labels: { entry_id: number; title: string }[]) => {
    finish.mutate(
      { blockId, carry_item_ids: carryIds, label_client_entries: labels },
      {
        onSuccess: () => {
          void focus.refetch();
          onExit();
        },
      },
    );
  };

  const endsAt =
    block.data?.phase === 'planning'
      ? block.data.planning_ends_at
      : block.data?.phase === 'wind_down'
        ? block.data.wind_down_ends_at
        : focus.data?.session_ends_at ?? null;

  const remaining = useCountdown(endsAt);
  const taskElapsed = useElapsed(focus.data?.task_started_at ?? null);
  const blockRemaining = useCountdown(block.data?.ends_at ?? null);

  // Past the bell the sprint keeps running, so the hero counts up instead of
  // sitting at zero: and every second of it is coming out of wind-down.
  const overage = useElapsed(
    block.data?.phase === 'break_pending' ? focus.data?.session_ends_at ?? null : null,
  );

  useFocusSessionSync(endsAt, remaining, () => {
    void block.refetch();
    void focus.refetch();
  });

  // The block covers the app rather than replacing it, so the page underneath
  // is still scrollable unless it is pinned. Distraction-free has to mean the
  // rest of the app cannot move behind the overlay.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    if (!block.data) return;
    setLightDay(block.data.light_day);
    setClientId(block.data.client_id);
    setProjectId(block.data.project_id);
  }, [block.data?.id, block.data?.light_day, block.data?.client_id, block.data?.project_id]);

  useEffect(() => {
    if (!seededSelection && suggestions.data && block.data?.phase === 'planning') {
      // Only pre-select meetings: tasks stay optional so you can start empty.
      setSelected(suggestions.data.filter((s) => s.required_meeting));
      setSeededSelection(true);
    }
  }, [suggestions.data, block.data?.phase, seededSelection]);

  const windDownRemaining = useMemo(() => {
    if (!block.data?.wind_down_ends_at) return 0;
    const left = Math.round(
      (new Date(block.data.wind_down_ends_at).getTime() - Date.now()) / 1000,
    );
    // Overage is only charged when the break is confirmed, so show the cost
    // as it accrues rather than letting the budget look untouched.
    return Math.max(0, left - overage);
  }, [block.data?.wind_down_ends_at, remaining, overage]);

  const backlogOptions = useMemo(() => {
    const inBlock = new Set(
      (block.data?.items ?? [])
        .map((i) => i.task_id)
        .filter((id): id is number => id != null),
    );
    return (suggestions.data ?? [])
      .filter((s) => s.task_id != null && !inBlock.has(s.task_id) && !s.required_meeting)
      .map((s) => ({
        taskId: s.task_id!,
        title: s.title,
        estimatedMinutes: s.estimated_minutes,
      }));
  }, [suggestions.data, block.data?.items]);

  if (!block.data || !focus.data) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface-sunken">
        <div className="h-32 w-64 animate-pulse rounded-lg bg-surface-2" />
      </div>
    );
  }

  const b = block.data;
  const phase = b.phase;

  const persistFocusClient = async (nextClient: number | null, nextProject: number | null) => {
    setClientId(nextClient);
    setProjectId(nextProject);
    await api(`/api/work-blocks/${blockId}`, {
      method: 'PATCH',
      json: { client_id: nextClient, project_id: nextProject },
    });
    void suggestions.refetch();
    void block.refetch();
  };

  const onConfirmPlanning = async () => {
    await updateItems.mutateAsync({
      blockId,
      items: selected.map((s) => ({
        task_id: s.task_id,
        calendar_event_id: s.calendar_event_id,
        time_block_id: s.time_block_id,
        estimated_minutes: s.estimated_minutes,
        required_meeting: s.required_meeting,
      })),
    });
    if (lightDay !== b.light_day) {
      await api(`/api/work-blocks/${blockId}`, {
        method: 'PATCH',
        json: { light_day: lightDay },
      });
    }
    await confirmPlanning.mutateAsync(blockId);
  };

  const onSwitchTask = (taskId: number) => {
    focusControls.switchTask.mutate({ task_id: taskId });
  };

  const onCompleteTask = async (taskId: number) => {
    const title = b.items.find((i) => i.task_id === taskId)?.title ?? null;
    const result = await api<{
      focus: typeof focus.data;
      previous_task_id: number | null;
      previous_task_title: string | null;
    }>('/api/focus/complete-task', { method: 'POST', json: { task_id: taskId } });
    void focus.refetch();
    void block.refetch();
    void qc.invalidateQueries({ queryKey: blockKeys.all });
    void qc.invalidateQueries({ queryKey: taskKeys.all });

    // Both prompts can be owed at once. Notes first: it is about the work
    // just finished, while resuming is about what happens next.
    const prevId = result.previous_task_id;
    if (prevId) {
      setResumeMeta({
        id: prevId,
        title: result.previous_task_title,
        reason: 'complete',
      });
    }
    askForNote(taskId, title, {
      onDone: () => {
        if (prevId) setResumeOpen(true);
      },
    });
  };

  const onCapture = async () => {
    const text = capture.trim();
    if (!text) return;
    const result = await captureTask.mutateAsync({
      blockId,
      title: text,
      client_id: clientId ?? undefined,
    });
    setCapture('');
    const added = [...result.items].reverse().find((i) => i.task_id);
    if (added?.task_id) {
      focusControls.switchTask.mutate({ task_id: added.task_id });
    }
    toast.show(`Added "${text}" to this block`, { tone: 'success' });
  };

  const onPauseTask = () => {
    focusControls.pauseTask.mutate(undefined, {
      onSuccess: () => void focus.refetch(),
    });
  };

  const onEndClientTimer = () => {
    const prevId = focus.data?.previous_task_id ?? null;
    const prevTitle = focus.data?.previous_task_title ?? null;
    focusControls.pauseTask.mutate(undefined, {
      onSuccess: () => {
        void focus.refetch();
        if (prevId && prevTitle) {
          setResumeMeta({ id: prevId, title: prevTitle, reason: 'client_end' });
          setResumeOpen(true);
        } else {
          toast.show('Client timer ended');
        }
      },
    });
  };

  const onAddFromBacklog = async (taskId: number, estimatedMinutes: number) => {
    await addItem.mutateAsync({
      blockId,
      task_id: taskId,
      estimated_minutes: estimatedMinutes,
      required_meeting: false,
    });
    setBacklogPickerOpen(false);
    focusControls.switchTask.mutate({ task_id: taskId });
    toast.show('Added to sprint', { tone: 'success' });
  };

  // The sprint only counts on past the bell while its interval is still open;
  // once the grace window closes there is no overage left to show.
  const showOverage = phase === 'break_pending' && Boolean(focus.data.session_ends_at);

  const hasTaskTimer = Boolean(focus.data?.active_task_id);
  const hasClientTimer = Boolean(
    focus.data?.active_client_id && !focus.data?.active_task_id,
  );

  const incompleteItems = b.items.filter((i) => !i.completed_at && !i.skipped);

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-surface-sunken text-text">
      <header className="flex items-center justify-between border-b border-border bg-surface-1 px-5 py-4">
        <div>
          <p className="text-label uppercase text-text-tertiary">Work block</p>
          <h1 className="text-title font-semibold">{PHASE_LABELS[phase] ?? phase}</h1>
        </div>
        <div className="flex gap-2">
          {onMinimize && phase !== 'planning' && (
            <Button variant="ghost" onClick={onMinimize} aria-label="Minimize work block">
              <Minimize2 size={16} aria-hidden />
            </Button>
          )}
          {phase === 'planning' && (
            <Button variant="ghost" onClick={() => setConfirmCancel(true)}>
              Cancel
            </Button>
          )}
          {phase !== 'review' && phase !== 'planning' && (
            <Button variant="ghost" onClick={() => setConfirmEndEarly(true)}>
              End early
            </Button>
          )}
          {phase === 'review' && (
            <Button
              variant="ghost"
              loading={finish.isPending}
              onClick={() => finishAndExit([], [])}
            >
              Done
            </Button>
          )}
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-6 overflow-y-auto px-5 py-8">
        {/* Timer hero */}
        {phase !== 'planning' && phase !== 'review' && (
          <div className="text-center">
            <p
              className={cn(
                'tabular text-[4rem] font-light leading-none tracking-tight',
                showOverage && 'text-warning',
              )}
            >
              {showOverage ? `+${formatCountdown(overage)}` : formatCountdown(remaining)}
            </p>
            {(phase === 'sprint' || phase === 'break_pending') && (
              <p className="mt-2 text-body text-text-secondary">
                {showOverage ? 'Over the sprint' : `Sprint ${b.sprint_index + 1}`} ·
                Wind-down budget {formatHms(windDownRemaining)}
              </p>
            )}
            {focus.data.active_task_title && (
              <p className="mt-4 text-title font-medium">{focus.data.active_task_title}</p>
            )}
            {focus.data.active_calendar_event_title && (
              <p className="mt-4 text-title font-medium">{focus.data.active_calendar_event_title}</p>
            )}
            {focus.data.active_client_name &&
              !focus.data.active_task_id &&
              !focus.data.active_calendar_event_id && (
              <p className="mt-4 text-title font-medium">
                {focus.data.active_client_name}
                {focus.data.active_project_name
                  ? ` · ${focus.data.active_project_name}`
                  : ''}
              </p>
            )}
            {!focus.data.active_task_id &&
              !focus.data.active_calendar_event_id &&
              !focus.data.active_client_id &&
              phase === 'sprint' && (
              <p className="mt-2 text-small text-warning">Sprint running · no timer</p>
            )}
            {(focus.data.active_task_id ||
              focus.data.active_calendar_event_id ||
              focus.data.active_client_id) && (
              <p className="tabular mt-1 text-small text-text-secondary">
                {focus.data.active_client_id && !focus.data.active_task_id
                  ? 'Client'
                  : 'Task'}{' '}
                {formatClock(Math.max(taskElapsed, focus.data.task_elapsed_seconds))}
              </p>
            )}
          </div>
        )}

        {phase !== 'planning' && phase !== 'review' && (
          <BlockProgress block={b} remaining={blockRemaining} timezone={timezone} />
        )}

        {phase === 'planning' && (
          <WorkBlockPlanner
            suggestions={suggestions.data ?? []}
            selected={selected}
            sprintBudgetMinutes={b.sprint_budget_minutes}
            onChange={setSelected}
            onConfirm={() => void onConfirmPlanning()}
            confirming={confirmPlanning.isPending || updateItems.isPending}
            lightDay={lightDay}
            onLightDayChange={setLightDay}
            clientId={clientId}
            projectId={projectId}
            onClientChange={(c, p) => void persistFocusClient(c, p)}
          />
        )}

        {phase === 'sprint' && (
          <>
            <TaskList
              block={b}
              activeTaskId={focus.data.active_task_id}
              activeEventId={focus.data.active_calendar_event_id}
              onSwitch={onSwitchTask}
              onStartEvent={(eventId) => {
                focusControls.startEvent.mutate({ calendar_event_id: eventId });
              }}
              onComplete={onCompleteTask}
            />
            {b.items.filter((i) => i.task_id || i.calendar_event_id).length === 0 && (
              <EmptySprintPanel onClientTimer={() => setClientPickerOpen(true)} />
            )}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {hasTaskTimer && (
                <Button variant="secondary" onClick={onPauseTask}>
                  <Pause size={16} aria-hidden /> Pause task
                </Button>
              )}
              {hasClientTimer ? (
                <Button variant="secondary" onClick={onEndClientTimer}>
                  <Square size={16} aria-hidden /> End client timer
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => setClientPickerOpen(true)}>
                  <Phone size={16} aria-hidden /> Client timer
                </Button>
              )}
              <Button variant="secondary" onClick={() => extend.mutate({ blockId, minutes: 5 })}>
                <Plus size={16} aria-hidden /> +5 min
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                variant="ghost"
                className="text-small"
                onClick={() => {
                  void suggestions.refetch();
                  setBacklogPickerOpen(true);
                }}
              >
                <ListPlus size={16} aria-hidden /> From backlog
              </Button>
            </div>
            <CaptureField
              value={capture}
              onChange={setCapture}
              onSubmit={() => void onCapture()}
              disabled={captureTask.isPending}
              placeholder="Add a task to this block…"
            />
          </>
        )}

        {phase === 'break_pending' && (
          <div className="flex flex-col gap-4 text-center">
            <Coffee size={48} className="mx-auto text-accent" strokeWidth={1.5} aria-hidden />
            <p className="text-body">Sprint&apos;s up. Ready for a break?</p>
            <p className="-mt-2 text-small text-text-tertiary">
              The clock keeps running until you answer, and the extra comes out of
              wind-down.
            </p>
            <Button loading={confirmBreak.isPending} onClick={() => confirmBreak.mutate(blockId)}>
              Take break
            </Button>
            <Button variant="secondary" onClick={() => extend.mutate({ blockId, minutes: 5 })}>
              Stay in flow (+5 min)
            </Button>
          </div>
        )}

        {phase === 'break' && (
          <div className="flex flex-col gap-4 text-center">
            <Coffee size={48} className="mx-auto text-accent" strokeWidth={1.5} aria-hidden />
            <p className="text-body text-text-secondary">Rest: the break ends on its own.</p>
            <Button
              variant="secondary"
              loading={endBreak.isPending}
              onClick={() => endBreak.mutate(blockId)}
            >
              <Undo2 size={16} aria-hidden /> Back early
            </Button>
            {remaining > 0 && (
              <p className="-mt-2 text-small text-text-tertiary">
                Cutting it short adds the remaining{' '}
                <span className="tabular">{formatCountdown(remaining)}</span> to your
                wind-down budget.
              </p>
            )}
          </div>
        )}

        {phase === 'return_pending' && (
          <div className="flex flex-col gap-3">
            <p className="text-body font-medium">What are you working on next?</p>
            <TaskList
              block={b}
              activeTaskId={null}
              activeEventId={null}
              onSwitch={(id) => confirmReturn.mutate({ blockId, task_id: id })}
              onStartEvent={(eventId) => {
                focusControls.startEvent.mutate({ calendar_event_id: eventId });
              }}
              onComplete={onCompleteTask}
            />
            {b.items.filter((i) => i.task_id || i.calendar_event_id).length === 0 && (
              <EmptySprintPanel onClientTimer={() => setClientPickerOpen(true)} />
            )}
            <CaptureField
              value={capture}
              onChange={setCapture}
              onSubmit={() => void onCapture()}
              disabled={captureTask.isPending}
              placeholder="Add a task to this block…"
            />
            <Button onClick={() => confirmReturn.mutate({ blockId })}>Continue</Button>
          </div>
        )}

        {phase === 'wind_down' && (
          <div className="text-center">
            <p className="text-body text-text-secondary">
              Transition time: plan your next block when ready.
            </p>
            <p className="tabular mt-4 text-title">{formatHms(windDownRemaining)} left</p>
          </div>
        )}

        {phase === 'review' && (
          <ReviewPanel
            block={b}
            incompleteItems={incompleteItems}
            onFinish={finishAndExit}
            finishing={finish.isPending}
          />
        )}

        {phase !== 'planning' && phase !== 'review' && <ActivityTally block={b} />}
      </main>

      <ClientTimerPicker
        open={clientPickerOpen}
        onClose={() => setClientPickerOpen(false)}
        onSelect={(cid, pid) => {
          focusControls.startClient.mutate({ client_id: cid, project_id: pid });
        }}
      />

      <BacklogAddPicker
        open={backlogPickerOpen}
        onClose={() => setBacklogPickerOpen(false)}
        options={backlogOptions}
        loading={addItem.isPending}
        onPick={(taskId, estimatedMinutes) => {
          void onAddFromBacklog(taskId, estimatedMinutes);
        }}
      />

      <TaskResumeSheet
        open={resumeOpen}
        previousTaskTitle={resumeMeta.title}
        body={
          resumeMeta.reason === 'client_end'
            ? 'Client timer ended. Resume the task you were on?'
            : undefined
        }
        resumeLabel={
          resumeMeta.title ? `Resume ${resumeMeta.title}` : undefined
        }
        dismissLabel={
          resumeMeta.reason === 'client_end' ? 'Leave paused' : 'Choose different task'
        }
        onResumePrevious={() => {
          focusControls.resumePrevious.mutate(undefined, {
            onSuccess: () => {
              setResumeOpen(false);
              setResumeMeta({ id: null, title: null, reason: 'complete' });
            },
          });
        }}
        onChooseDifferent={() => {
          focusControls.clearPrevious.mutate(undefined, {
            onSuccess: () => {
              setResumeOpen(false);
              setResumeMeta({ id: null, title: null, reason: 'complete' });
            },
          });
        }}
        onClose={() => setResumeOpen(false)}
      />

      <ConfirmSheet
        open={confirmCancel}
        title="Cancel this work block?"
        body="Nothing is saved. You can start again whenever you are ready."
        confirmLabel="Cancel block"
        danger
        loading={cancelBlock.isPending}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() => {
          cancelBlock.mutate(blockId, {
            onSuccess: () => {
              setConfirmCancel(false);
              exitBlock();
            },
          });
        }}
      />

      <ConfirmSheet
        open={confirmEndEarly}
        title="End this block early?"
        body="You will jump to the review step so you can carry unfinished work forward."
        confirmLabel="End early"
        loading={endEarly.isPending}
        onClose={() => setConfirmEndEarly(false)}
        onConfirm={() => {
          endEarly.mutate(blockId, {
            onSuccess: () => {
              setConfirmEndEarly(false);
              void block.refetch();
            },
          });
        }}
      />
    </div>
  );
}

/** How much of the whole block is left, not just the current interval. */
function BlockProgress({
  block,
  remaining,
  timezone,
}: {
  block: WorkBlock;
  remaining: number;
  timezone: string;
}) {
  const start = new Date(block.starts_at).getTime();
  const total = new Date(block.ends_at).getTime() - start;
  const elapsed = total > 0 ? (Date.now() - start) / total : 0;
  const pct = Math.min(100, Math.max(0, elapsed * 100));
  const endsAtLabel = new Date(block.ends_at).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });

  return (
    <div>
      <div className="flex items-baseline justify-between text-label text-text-tertiary">
        <span>Block ends {endsAtLabel}</span>
        <span className="tabular text-text-secondary">{formatHms(remaining)} left</span>
      </div>
      <div
        role="progressbar"
        aria-label="Work block progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-surface-3"
      >
        <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * The block's running tab: what the time went to, as it happens.
 *
 * Server totals cover closed entries only, so the row still running gets its
 * live remainder added here rather than waiting for the next poll.
 */
function ActivityTally({ block }: { block: WorkBlock }) {
  const rows = block.activity ?? [];
  const running = rows.find((r) => r.running_since);
  const live = useElapsed(running?.running_since ?? null);

  if (rows.length === 0) return null;

  const seconds = (row: BlockActivity) => row.seconds + (row.running_since ? live : 0);
  const total = rows.reduce((sum, row) => sum + seconds(row), 0);

  return (
    <section
      aria-labelledby="block-tally-heading"
      className="rounded-lg border border-border bg-surface-1"
    >
      <div className="flex items-baseline justify-between px-3 py-2">
        <h2
          id="block-tally-heading"
          className="text-label uppercase tracking-wide text-text-tertiary"
        >
          Worked this block
        </h2>
        <span className="tabular text-small font-medium">{formatClock(total)}</span>
      </div>
      <ul className="border-t border-border">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2 px-3 py-2">
            <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
              {row.running_since ? (
                <span className="size-dot animate-pulse rounded-full bg-accent" />
              ) : row.completed ? (
                <Check size={14} className="text-success" />
              ) : null}
            </span>
            <span className="min-w-0 flex-1 truncate text-small">
              {row.title}
              {row.running_since && <span className="sr-only"> (running)</span>}
              {row.completed && <span className="sr-only"> (done)</span>}
            </span>
            <span className="tabular text-small text-text-secondary">
              {formatClock(seconds(row))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TaskList({
  block,
  activeTaskId,
  activeEventId,
  onSwitch,
  onStartEvent,
  onComplete,
}: {
  block: WorkBlock;
  activeTaskId: number | null;
  activeEventId: number | null;
  onSwitch: (taskId: number) => void;
  onStartEvent: (eventId: number) => void;
  onComplete: (taskId: number) => void;
}) {
  const taskItems = block.items.filter((i) => i.task_id);
  const meetingItems = block.items.filter((i) => i.calendar_event_id);
  if (taskItems.length === 0 && meetingItems.length === 0) return null;

  return (
    <ul className="flex flex-col gap-1">
      {meetingItems.map((item) => (
        <li
          key={item.id}
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2',
            activeEventId === item.calendar_event_id ? 'bg-accent-quiet' : 'hover:bg-surface-2',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-body">{item.title}</span>
          {activeEventId !== item.calendar_event_id && (
            <button
              type="button"
              className="pressable rounded p-1 text-accent"
              aria-label="Start meeting timer"
              onClick={() => item.calendar_event_id && onStartEvent(item.calendar_event_id)}
            >
              <Play size={16} />
            </button>
          )}
        </li>
      ))}
      {taskItems.map((item) => (
        <li
          key={item.id}
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2',
            activeTaskId === item.task_id ? 'bg-accent-quiet' : 'hover:bg-surface-2',
          )}
        >
          <button
            type="button"
            className="min-w-0 flex-1 truncate text-left text-body"
            onClick={() => item.task_id && onSwitch(item.task_id)}
          >
            {item.title}
          </button>
          {item.completed_at ? (
            <Check size={16} className="text-success" aria-hidden />
          ) : (
            <>
              {activeTaskId !== item.task_id && (
                <button
                  type="button"
                  className="pressable rounded p-1 text-accent"
                  aria-label="Start timer"
                  onClick={() => item.task_id && onSwitch(item.task_id)}
                >
                  <Play size={16} />
                </button>
              )}
              <button
                type="button"
                className="pressable rounded p-1 text-text-secondary"
                aria-label="Mark done"
                onClick={() => item.task_id && onComplete(item.task_id)}
              >
                <Check size={16} />
              </button>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function EmptySprintPanel({ onClientTimer }: { onClientTimer: () => void }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-1 px-4 py-5 text-center">
      <p className="text-body text-text-secondary">No tasks in this block yet.</p>
      <p className="mt-1 text-small text-text-tertiary">
        Add one below, or start a client timer for work you&apos;ll label when the block ends.
      </p>
      <Button variant="secondary" className="mt-3" onClick={onClientTimer}>
        <Phone size={16} aria-hidden /> Client timer
      </Button>
    </div>
  );
}

function CaptureField({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder = 'Capture a thought…',
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="mt-auto"
    >
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-surface-1 px-3 py-2 text-body disabled:opacity-50"
      />
    </form>
  );
}

function ReviewPanel({
  block,
  incompleteItems,
  onFinish,
  finishing,
}: {
  block: WorkBlock;
  incompleteItems: WorkBlock['items'];
  onFinish: (carryIds: number[], labels: { entry_id: number; title: string }[]) => void;
  finishing: boolean;
}) {
  const [carry, setCarry] = useState<number[]>(incompleteItems.map((i) => i.id));
  const [labels, setLabels] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    for (const e of block.unlabeled_client_entries ?? []) {
      init[e.id] = '';
    }
    return init;
  });

  const toggleCarry = (id: number) => {
    setCarry((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const unlabeled = block.unlabeled_client_entries ?? [];

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-body font-semibold">Block review</h2>
      <p className="text-small text-text-secondary">
        {block.items.filter((i) => i.completed_at).length} of {block.items.length} items completed
        {block.ended_early && ' · ended early'}
      </p>

      <ActivityTally block={block} />

      {incompleteItems.length > 0 && (
        <div>
          <p className="mb-2 text-small font-medium">Need more time?</p>
          <p className="mb-2 text-label text-text-tertiary">
            Unchecked items stay on the calendar with remaining time (logged work is subtracted).
          </p>
          <ul className="flex flex-col gap-1">
            {incompleteItems.map((item) => (
              <li key={item.id}>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-surface-2">
                  <input
                    type="checkbox"
                    checked={carry.includes(item.id)}
                    onChange={() => toggleCarry(item.id)}
                    className="size-5 accent-accent"
                  />
                  <span className="text-body">{item.title}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {unlabeled.length > 0 && (
        <div>
          <p className="mb-2 text-small font-medium">Client time to label</p>
          <ul className="flex flex-col gap-2">
            {unlabeled.map((entry) => (
              <UnlabeledEntryRow
                key={entry.id}
                entry={entry}
                value={labels[entry.id] ?? ''}
                onChange={(title) => setLabels((prev) => ({ ...prev, [entry.id]: title }))}
              />
            ))}
          </ul>
        </div>
      )}

      <Button
        loading={finishing}
        onClick={() => {
          const labelPayload = unlabeled
            .filter((e) => labels[e.id]?.trim())
            .map((e) => ({ entry_id: e.id, title: labels[e.id]!.trim() }));
          onFinish(carry, labelPayload);
        }}
      >
        Finish block
      </Button>
    </div>
  );
}

function UnlabeledEntryRow({
  entry,
  value,
  onChange,
}: {
  entry: UnlabeledClientEntry;
  value: string;
  onChange: (title: string) => void;
}) {
  return (
    <li className="rounded-lg border border-border bg-surface-1 px-3 py-2">
      <p className="text-small text-text-secondary">
        {entry.client_name} · {Math.round(entry.duration_seconds / 60)}m logged
      </p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="What was this work?"
        className="mt-2 w-full rounded border border-border bg-surface-1 px-2 py-1.5 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
      />
    </li>
  );
}
